import { Permission } from '@Constants/Permission.g';
import { MESSAGES_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { useHasPermission } from '@Contexts/appContext';
import { useTseClient } from '@Hooks/Billing';
import {
    Card,
    EditFrameMode,
    EditFrameShell,
    FrameEditBase,
    Grid,
    GridColumn,
    GridColumns,
    IFrameBodyProps,
    IFrameEditShellProps,
    TextInput,
    isNullOrEmpty,
    UnlabeledField,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useProp,
    useTranslate,
} from '@synqo/frontend-component-library';
import { SecretField } from '@Components/SecretField';
import { ApiCashRegister } from '@Model';
import { useParams } from 'react-router';
import { TestTseButton } from '../TestTseButton';

export function FrameEditCashRegister(props: IFrameEditShellProps<ApiCashRegister>) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiCashRegister>
            apiResourcePath={`${MESSAGES_API}/cash_registers`}
            renderer={FrameEditCashRegisterBody}
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            canEdit={props.canEdit !== false && hasPermission(Permission.MessagesCashRegistersUpdate)}
            canCreate={props.canCreate !== false && hasPermission(Permission.MessagesCashRegistersCreate)}
            canDelete={props.canDelete !== false && hasPermission(Permission.MessagesCashRegistersDelete)}
            frameProps={{
                events: {
                    ...frameEditBaseNavigationEvents,
                    ...props.events,
                },
            }}
        />
    );
}

function FrameEditCashRegisterBody(props: IFrameBodyProps<ApiCashRegister>) {
    const i18n = useTranslate('');

    const apiCashRegister = props.data;

    const binder = useGuiBinder<ApiCashRegister>({
        ...props,
        canEdit: props.mode !== EditFrameMode.View,
    });

    const labelField = useProp(binder, 'label');
    const remarksField = useProp(binder, 'remarks');

    const hasTseId = !isNullOrEmpty(apiCashRegister.tse_client_id);
    const tseClient = useTseClient(hasTseId ? apiCashRegister.id : '', props.mode === EditFrameMode.View);

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel('Kasse', props.mode),
                resourceLabel: 'Kasse',
                ...props.events,
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card name="maindata" border={'withoutBorder'}>
                <Grid>
                    <GridColumns spans={[4, 4, 'content']}>
                        <TextInput {...labelField} label={i18n('Bezeichnung')} />
                        {props.mode !== EditFrameMode.Create && (
                            <SecretField
                                label={i18n('TSE Client Api Key')}
                                value={apiCashRegister.tse_client_api_key}
                            />
                        )}
                        {hasTseId && (
                            <UnlabeledField>
                                <TestTseButton isTseAvailable={tseClient.isTseAvailable} onPing={tseClient.pingTse} />
                            </UnlabeledField>
                        )}
                    </GridColumns>
                </Grid>
                <Grid>
                    <GridColumn>
                        <TextInput {...remarksField} inputSize={3} label={i18n('Bemerkung')} />
                    </GridColumn>
                </Grid>
            </Card>
        </EditFrameShell>
    );
}
